<template>
  <div class="admin desktop">
    <h1 style="font-size: 19pt; font-weight: 600" class="mb-4">Subscription</h1>
    <div class="subscription" v-if="!loading.general && userProfile">
      <div class="d-flex">
        <v-card
          style="
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
            min-width: 600px;
            width: 820px;
          "
          class="pa-6"
        >
          <div>
            <h2>
              Subscription<span v-if="userProfile?.subscription?.trial">
                : Free Trial</span
              >
              <v-chip
                class="button ml-2 font-weight-regular"
                variant="flat"
                color="button"
                size="small"
                v-if="userProfile?.subscription?.status === 'active'"
                >Active</v-chip
              >
            </h2>

            <div
              v-if="
                userProfile?.subscription &&
                userProfile?.subscription?.status === 'active'
              "
            >
              <p style="font-size: 10pt" class="ma-0 mt-2 text-muted">
                You're currently on the
                <b>{{ userProfile?.subscription?.plan || "trial" }}</b>
                plan.
                <span v-if="userProfile?.subscription?.trial"
                  >Create workspaces for up to 10 brands. Your trial ends on
                  <b>{{ endDate() }}</b
                  >.</span
                >
                <span v-else-if="userProfile?.subscription?.plan">
                  Create workspaces for up to
                  <b>{{ userProfile?.subscription?.brands }}</b> brands.
                </span>
              </p>
              <v-btn
                @click="
                  openLink(
                    env === 'production'
                      ? `https://billing.stripe.com/p/login/dRm9AT0JDaTU7VlbeybV600`
                      : `https://billing.stripe.com/p/login/test_dRm9AT0JDaTU7VlbeybV600`,
                  )
                "
                v-if="
                  userProfile?.subscription?.status === 'active' &&
                  userProfile?.subscription?.trial === false
                "
                color="smokeDark"
                size="small"
                elevation="0"
                class="mt-6 px-6"
                >Manage subscription & invoices</v-btn
              >
            </div>
            <div v-else>
              <p style="font-size: 14px" class="ma-0 mt-2">
                You don't have a subscription yet.
                <b>Get one now</b>
              </p>
            </div>
          </div>
        </v-card>
      </div>

      <div class="mt-4">
        <v-card
          flat
          style="
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
            min-width: 600px;
            width: 820px;
          "
        >
          <div class="px-6 pb-4">
            <h2 class="mb-2 mt-4">
              Workspaces
              <v-chip class="ml-2">
                <span style="font-size: 10pt; font-weight: 400"
                  >{{ userProfile?.subscription?.workspaces?.length }}/{{
                    userProfile?.subscription?.brands
                  }}</span
                >
              </v-chip>
            </h2>
            <p style="font-size: 10pt; font-weight: 400" class="text-muted">
              You can add more workspaces, but your plan allows you to have
              <b>{{ userProfile?.subscription?.brands }}</b> active workspace at
              a time. Select the workspace you want to use.
            </p>

            <div class="mt-4" style="gap: 15px">
              <div
                v-for="workspace in workspaces"
                :key="workspace.uid"
                style="
                  display: grid;
                  grid-template-columns: auto 60px;
                  gap: 15px;
                "
                class="mb-2"
              >
                <v-card
                  class="py-2 px-3"
                  flat
                  :disabled="!userProfile?.subscription?.active"
                  @click="toggleWorkspace(workspace)"
                  :color="
                    userProfile?.subscription?.workspaces?.includes(
                      workspace.uid,
                    )
                      ? 'smokeDark'
                      : ''
                  "
                  width="100%"
                  style="
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                  "
                >
                  <div class="d-flex align-center justify-space-between">
                    <div class="d-flex align-center" style="gap: 10px">
                      <v-icon size="small" class="mr-2">{{
                        userProfile?.subscription?.workspaces?.includes(
                          workspace.uid,
                        ) || workspace.subscription
                          ? "mdi-check-circle"
                          : "mdi-circle-outline"
                      }}</v-icon>

                      {{ workspace.about.name }}
                      <span
                        class="text-muted"
                        style="font-size: 9pt"
                        v-if="workspace.subscription"
                        >(
                        {{
                          workspace.subscription.plan
                            ? workspace.subscription.plan
                            : "Free trial"
                        }}
                        <span
                          class="text-muted"
                          v-if="workspace.subscription.plan"
                          >plan</span
                        >
                        - {{ workspace.subscription.user_email }})</span
                      >
                    </div>
                  </div>
                </v-card>

                <v-dialog max-width="500">
                  <template v-slot:activator="{ props: activatorProps }">
                    <v-tooltip
                      :text="
                        workspace.subscription?.active
                          ? `Remove subscription from this workspace to delete it`
                          : `Only ${workspace.members[0].email} can delete the workspace`
                      "
                    >
                      <template v-slot:activator="{ props }">
                        <div v-bind="props">
                          <v-btn
                            :disabled="
                              workspace.subscription ||
                              workspace.members[0].uid != userProfile.uid
                            "
                            height="42"
                            variant="text"
                            size="small"
                            v-bind="activatorProps"
                            ><v-icon>mdi-delete-outline</v-icon></v-btn
                          >
                        </div>
                      </template>
                    </v-tooltip>
                  </template>

                  <template v-slot:default="{ isActive }">
                    <v-card title="Delete workspace">
                      <v-card-text>
                        Are you sure you want to delete this workspace? This
                        action cannot be undone.
                      </v-card-text>

                      <v-card-actions>
                        <v-spacer></v-spacer>

                        <v-btn
                          text="Close Dialog"
                          @click="isActive.value = false"
                        ></v-btn>
                        <v-btn
                          text="Delete Workspace"
                          @click="
                            $store.dispatch('deleteWorkspace', workspace.uid)
                          "
                        ></v-btn>
                      </v-card-actions>
                    </v-card>
                  </template>
                </v-dialog>
              </div>

              <v-card
                class="pa-2 d-flex align-center justify-center"
                flat
                @click="$router.push('/create-workspace')"
                style="
                  border: 1px solid #e5e7eb;
                  border-radius: 8px;
                  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                  width: 100%;
                "
              >
                <div
                  style="
                    display: grid;
                    grid-template-columns: 30px 1fr;
                    align-items: center;
                    font-size: 10pt;
                  "
                >
                  <v-icon size="small" class="mr-2">mdi-plus</v-icon>
                  <div>Create new workspace</div>
                </div>
              </v-card>
            </div>
            <div class="mt-4" v-if="userProfile?.invites?.length > 0">
              <p
                style="font-size: 10pt; font-weight: 400"
                class="text-muted mb-2"
              >
                You are invited to join the following workspaces:
              </p>
              <div v-for="inv in userProfile.invites" :key="inv.workspace">
                <v-card
                  class="px-4 py-2"
                  style="
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
                    width: 100%;
                  "
                  flat
                >
                  <div class="d-flex align-center justify-space-between">
                    <div class="d-flex align-center" style="gap: 10px">
                      {{ inv.name }}
                      <span class="text-muted"
                        >invited by {{ inv.invited_by }}</span
                      >
                    </div>
                    <div class="d-flex align-center" style="gap: 10px">
                      <v-btn
                        variant="text"
                        size="small"
                        icon="mdi-check-circle"
                        @click="acceptInvite(inv)"
                      ></v-btn>
                      <v-btn
                        variant="text"
                        size="small"
                        icon="mdi-close"
                        @click="declineInvite(inv)"
                      ></v-btn>
                    </div>
                  </div>
                </v-card>
              </div>
            </div>
          </div>
        </v-card>
      </div>

      <div
        style="
          border: 1px solid #e5e7eb;
          padding: 20px;
          border-radius: 8px;
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
          width: 820px;
        "
        class="mt-4 mb-10"
      >
        <h2 class="mb-4 mt-2 ml-2">Plans</h2>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px">
          <v-card
            class="pa-6"
            v-for="plan in plans"
            :key="plan.name"
            flat
            style="
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
            "
          >
            <h2 class="font-weight-bold" style="font-size: 16px">
              {{ plan.name }}
            </h2>
            <p style="font-size: 10pt" class="text-muted">
              {{ plan.description }}
            </p>
            <p style="font-size: 10pt" class="mt-4 text-muted">
              {{ plan.brands }} brand<span v-if="plan.brands > 1">s</span>
            </p>
            <h2 class="font-weight-bold" style="font-size: 24px">
              ${{ plan.price }}
              <span
                style="font-size: 10pt"
                class="text-muted font-weight-regular"
                >/month</span
              >
            </h2>
            <v-btn
              v-if="userProfile?.subscription?.plan !== plan.name"
              @click="
                userProfile?.subscription?.plan === null
                  ? createCheckoutSession(plan)
                  : openLink(
                      env === 'production'
                        ? `https://billing.stripe.com/p/login/dRm9AT0JDaTU7VlbeybV600`
                        : `https://billing.stripe.com/p/login/test_dRm9AT0JDaTU7VlbeybV600`,
                    )
              "
              color="primary"
              :loading="plan.loading"
              elevation="0"
              size="small"
              block
              class="mt-4"
              >{{
                userProfile?.subscription?.plan === null
                  ? "Subscribe"
                  : "Change plan"
              }}</v-btn
            >

            <v-chip
              label
              v-else
              color="success"
              variant="flat"
              size="small"
              class="mt-4"
              >Current plan</v-chip
            >
          </v-card>
        </div>
      </div>

      <div>
        <!-- <v-card
          v-if="workspace && workspace.subscription"
          flat
          :style="{
            borderBottom: '3px solid #272727',
            borderLeft: '1px solid #272727',
            borderTop: '1px solid #272727',
            borderRight: '3px solid #272727',
            borderRadius: '8px',
          }"
          width="50vw"
          max-width="800"
          class="mb-4"
        >
          <div>
            <v-sheet
              class="d-flex justify-center align-center py-2"
              color="accent"
              style="
                font-size: 10pt;
                font-weight: 600;
                border-top-right-radius: 7px;
                border-top-left-radius: 7px;
              "
            >
              Invoices
            </v-sheet>
            <v-divider class="primary"></v-divider>
          </div>

          <div style="width: 100%">
            <v-data-table
              class=""
              :headers="headersSub"
              :items="itemsSub"
              hide-default-header
              mobile-breakpoint="100"
              :hide-default-footer="itemsSub.length > 0"
            >
              <template v-slot:[`item.amount`]="{ item }">
                <span>${{ item.amount }}</span>
              </template>

              <template v-slot:[`item.status`]="{ item }">
                <v-chip
                  size="small"
                  variant="flat"
                  :color="item.status === 'paid' ? 'success' : 'info'"
                  >{{ capitalize(item.status) }}</v-chip
                >
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn variant="text" size="small" icon @click="download(item)">
                  <v-icon color="primary" size="small"> mdi-download </v-icon>
                </v-btn>
              </template>
            </v-data-table>
            <div>
              <v-divider></v-divider>
              <div class="px-4 pb-2">
                <p style="font-size: 10px" class="mb-2 mt-4">
                  Send invoices to:
                </p>
                <v-text-field
                  class="mb-2"
                  label="invoices@yourcompany.com"
                  hide-details=""
                  variant="solo"
                  density="compact"
                  v-model="workspace.subscription.invoices_email"
                  flat
                  bg-color="smokeDark"
                  style="border-radius: 8px; font-size: 10pt"
                  @keypress="enter"
                >
                  <template v-slot:append-inner>
                    <v-btn
                      color="primary"
                      size="small"
                      elevation="0"
                      @click="updateEmail"
                      ><span class="text-white"> Save </span></v-btn
                    >
                  </template>
                </v-text-field>
              </div>
            </div>
          </div>
        </v-card> -->

        <!-- <v-card
        class="mt-4"
        max-width="500"
        flat
        :style="{
          borderBottom: '3px solid #272727',
          borderLeft: '1px solid #272727',
          borderTop: '1px solid #272727',
          borderRight: '3px solid #272727',
        }"
      >
        <div>
          <v-sheet
            class="d-flex justify-center align-center py-2"
            color="accent"
            style="
              font-size: 10pt;
              font-weight: 600;
              border-top-right-radius: 7px;
              border-top-left-radius: 7px;
            "
          >
            Free months with referrals
          </v-sheet>
          <v-divider class="primary"></v-divider>
        </div>
        <div class="startup">
          <v-img
            width="100"
            contain
            :src="require('@/assets/icons/rocket.jpg')"
          ></v-img>
          <div class="py-6 pl-2 pr-6">
            <p style="font-size: 10px" class="mb-1">REFERRAL PROGRAM</p>
            <h1>Get 3 month as a startup</h1>
            <p class="mb-1" style="font-size: 12px">
              Startups younger than <b>10</b> years are eligable for the
              program. Apply today for your discount.
            </p>
            <v-btn color="primary" size="small" class="mt-2" elevation="0"
              >Apply</v-btn
            >
          </div>
        </div>
      </v-card> -->
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { DateTime } from "luxon";
import { httpsCallable } from "firebase/functions";
import { functions, env } from "@/firebase";

export default {
  computed: {
    ...mapState({
      userProfile: (state) => state.users.userProfile,
      loading: (state) => state.loading,
      workspace: (state) => state.workspace.workspace,
      workspaces: (state) => state.workspace.workspaces,
      settings: (state) => state.settings,
      customerId: (state) => state.subscription.customer_id,
    }),
  },
  data() {
    return {
      env: env,
      showPlans: true,
      headersSub: [
        {
          text: "Invoices",
          align: "start",
          sortable: false,
          value: "month",
        },
        {
          text: "",
          align: "start",
          sortable: false,
          value: "amount",
        },
        {
          text: "",
          align: "start",
          sortable: false,
          value: "status",
        },
        {
          text: "",
          align: "end",
          sortable: false,
          value: "actions",
        },
      ],
      itemsSub: [
        {
          month: "January",
          status: "paid",
          amount: 29,
          discount: 60,
        },
      ],
      plans: [
        {
          name: "Basic",
          description: "For individuals and small teams",
          price: 49,
          brands: 1,
          price_id:
            env === "production"
              ? "price_1Sxs4qGOqiYyS6hdJpe0OiL9"
              : "price_1SSEUxGOqiYyS6hdCEa4N8a0",
          loading: false,
        },
        {
          name: "Growth",
          description: "For growing teams and businesses",
          price: 99,
          brands: 3,
          loading: false,
          price_id:
            env === "production"
              ? "price_1Sxs54GOqiYyS6hdb68uMQI5"
              : "price_1SSEW9GOqiYyS6hdmhivtXON",
        },
        {
          name: "Agency",
          description: "For growing agencies",
          price: 199,
          brands: 8,
          loading: false,
          price_id:
            env === "production"
              ? "price_1Sxs57GOqiYyS6hdGlrZJ2F0"
              : "price_1SYRRiGOqiYyS6hdcd1qr5BZ",
        },
        {
          name: "Agency Pro",
          description: "For large agencies and enterprises",
          price: 399,
          brands: 20,
          loading: false,
          price_id:
            env === "production"
              ? "price_1Sxs5aGOqiYyS6hd3fNtV5s7"
              : "price_1SYRRtGOqiYyS6hdBYuF4OmA",
        },
      ],
    };
  },
  methods: {
    async createCheckoutSession(plan) {
      plan.loading = true;
      try {
        const createCheckoutSession = httpsCallable(
          functions,
          "createCheckoutSession",
        );
        const session = await createCheckoutSession({
          price_id: plan.price_id,
          customer_id: this.customerId || null,
          customer_email: !this.customerId ? this.userProfile?.email : null,
        });
        console.log(session);
        this.openLink(session.data.url);
        plan.loading = false;
      } catch (error) {
        console.log(error);
        plan.loading = false;
        this.$store.commit("setNotification", {
          show: true,
          text: "Something went wrong creating checkout session",
          type: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
    endDate() {
      let trialEnd = "";

      const trialDays = this.settings?.trial?.days || 0;
      const createdAt = this.userProfile?.created_at || 0;
      const created = DateTime.fromSeconds(createdAt);
      trialEnd = created.plus({ days: trialDays });
      trialEnd = trialEnd.toFormat("dd/MM/yyyy");
      return trialEnd;
    },
    openLink(link) {
      window.open(link, "_blank");
    },
    capitalize(string) {
      let firstCharacter = string.charAt(0);
      firstCharacter = firstCharacter.toUpperCase();
      let capitalizedString = firstCharacter + string.slice(1);
      return capitalizedString;
    },
    download(i) {
      console.log(i);
    },
    enter(v) {
      if (v.key === "Enter") {
        this.updateEmail();
      }
    },
    updateEmail() {
      this.$store.dispatch("updateWorkspace", {
        type: "subscription.invoices_email",
        value: this.workspace.subscription.invoices_email,
      });
    },
    async toggleWorkspace(workspace) {
      if (
        workspace.subscription &&
        workspace.subscription.user != this.userProfile.uid
      ) {
        this.$store.commit("setNotification", {
          show: true,
          text: `You cannot change this workspace. It's subscription is managed by ${workspace.subscription.user_email}`,
          type: "error",
          icon: "mdi-alert-circle",
        });
        return;
      }
      if (!this.userProfile.subscription?.workspaces?.includes(workspace.uid)) {
        if (
          this.userProfile.subscription.workspaces.length >=
          this.userProfile.subscription.brands
        ) {
          this.$store.commit("setNotification", {
            show: true,
            text: "You have reached the maximum number of workspaces for your plan",
            type: "error",
            icon: "mdi-alert-circle",
          });
          return;
        }
        this.userProfile.subscription.workspaces.push(workspace.uid);
      } else {
        this.userProfile.subscription.workspaces =
          this.userProfile.subscription.workspaces.filter(
            (w) => w !== workspace.uid,
          );
      }
      await this.$store.dispatch(
        "updateSubscriptionWorkspaces",
        this.userProfile.subscription.workspaces,
      );
    },
    async acceptInvite(inv) {
      await this.$store.dispatch("acceptWorkspaceInvite", inv);
    },
    async declineInvite(inv) {
      await this.$store.dispatch("declineWorkspaceInvite", inv);
    },
  },
};
</script>
